import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { DatePicker, Message, Placeholder } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';

import {
  useCommitmentHistory,
  useCreateCommitment,
  useDeleteCommitment,
  useTawhPercents,
  useUpdateCommitment,
} from '../../hooks/useQueries';
import './CommitmentHistory.css';

// The dated history of what an employee is measured against: exempt at a
// percentage of TAWH, or non-exempt with target hours per pay period. Each
// change starts on a pay period (the 1st or the 16th) and holds until the next,
// and every pay period - past ones included - is measured against the one in
// force on its first day. That is what keeps an old View Hours screen and its
// validations right after somebody moves up or down.
//
// Like PayRateHistory, it owns its own read and edit modal; the profile's
// Employment card hosts it and drives "add" through the handle.

const pad = (n) => String(n).padStart(2, '0');

const toIso = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseIso = (iso) => {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
};

const formatDate = (iso) => {
  const date = parseIso(iso);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

// The first day of the pay period `date` falls in.
const payPeriodStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate() >= 16 ? 16 : 1);

const isPayPeriodStart = (date) => date.getDate() === 1 || date.getDate() === 16;

const formatNumber = (value) => Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 });

// The commitment in force today from a newest-first history: the first one not
// dated ahead of today, or the oldest when every one is. Null for no history.
export const currentCommitment = (rows, today = new Date()) => {
  if (!rows || rows.length === 0) return null;
  const todayIso = toIso(today);
  return rows.find((row) => row.effective_date <= todayIso) || rows[rows.length - 1];
};

export const describeCommitment = (row) => {
  if (!row) return '';
  if (row.is_part_time) {
    return row.target_hours == null
      ? 'Non-exempt · no target set'
      : `Non-exempt · ${formatNumber(row.target_hours)} hrs per pay period`;
  }
  return `Exempt · ${formatNumber(row.tawh_percent ?? 100)}% of TAWH`;
};

const emptyForm = () => ({
  effectiveDate: payPeriodStart(new Date()),
  isPartTime: false,
  tawhPercent: '100',
  targetHours: '',
  notes: '',
});

function CommitmentHistory({ employeeId, canManage = false, showAddButton = true }, ref) {
  const historyQuery = useCommitmentHistory(employeeId || 'me');
  const { data: tawhPercents = [] } = useTawhPercents();
  const createCommitment = useCreateCommitment();
  const updateCommitment = useUpdateCommitment();
  const deleteCommitment = useDeleteCommitment();

  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);

  const rows = useMemo(() => historyQuery.data || [], [historyQuery.data]);
  const editable = canManage && Boolean(employeeId);

  const todayIso = toIso(new Date());
  const currentId = currentCommitment(rows)?.id;

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      effectiveDate: parseIso(row.effective_date),
      isPartTime: Boolean(row.is_part_time),
      tawhPercent: row.tawh_percent == null ? '100' : String(Number(row.tawh_percent)),
      targetHours: row.target_hours == null ? '' : String(Number(row.target_hours)),
      notes: row.notes || '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  useImperativeHandle(ref, () => ({ canManage: editable, openAdd }));

  const closeModal = () => {
    setConfirmDeleteOpen(false);
    setModalOpen(false);
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormError(null);
  };

  const submit = async () => {
    if (!form.effectiveDate || !isPayPeriodStart(form.effectiveDate)) {
      setFormError('Pick the first day of a pay period - the 1st or the 16th.');
      return;
    }
    const body = {
      effective_date: toIso(form.effectiveDate),
      is_part_time: form.isPartTime,
      notes: form.notes.trim() || null,
    };
    if (form.isPartTime) {
      if (form.targetHours !== '') {
        const hours = Number(form.targetHours);
        if (!Number.isFinite(hours) || hours < 0 || hours > 200) {
          setFormError('Target hours must be between 0 and 200.');
          return;
        }
      }
      body.target_hours = form.targetHours === '' ? null : Number(form.targetHours);
    } else {
      const pct = form.tawhPercent === '' ? 100 : Number(form.tawhPercent);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
        setFormError('The percentage must be between 0 and 100.');
        return;
      }
      body.tawh_percent = pct;
    }

    try {
      if (editing?.id) {
        await updateCommitment.mutateAsync({ employeeId, commitmentId: editing.id, ...body });
      } else {
        await createCommitment.mutateAsync({ employeeId, ...body });
      }
      closeModal();
    } catch (err) {
      setFormError(String(err?.message || err));
    }
  };

  const remove = async () => {
    if (!editing?.id) return;
    try {
      await deleteCommitment.mutateAsync({ employeeId, commitmentId: editing.id });
      closeModal();
    } catch (err) {
      setConfirmDeleteOpen(false);
      setFormError(String(err?.message || err));
    }
  };

  const busy = createCommitment.isPending || updateCommitment.isPending || deleteCommitment.isPending;
  const effectiveIso = form.effectiveDate ? toIso(form.effectiveDate) : null;
  const reachesBack = Boolean(effectiveIso) && effectiveIso < toIso(payPeriodStart(new Date()));

  if (historyQuery.isLoading) return <Placeholder.Paragraph rows={3} active />;

  if (historyQuery.error) {
    return (
      <Message type="error" showIcon>
        {String(historyQuery.error?.message || historyQuery.error)}
      </Message>
    );
  }

  return (
    <div className="commitments">
      <div className="commitments__head">
        <span className="me-field__label">Commitment history</span>
        {showAddButton && editable ? (
          <button type="button" className="bdc-btn bdc-btn--sm" onClick={openAdd}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Record a change
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="text-muted small">No commitment recorded yet.</div>
      ) : (
        <ul className="commitments__list">
          {rows.map((row) => {
            const isCurrent = row.id === currentId;
            const isUpcoming = row.effective_date > todayIso;
            return (
              <li key={row.id} className={`commitments__row${isCurrent ? ' is-current' : ''}`}>
                <div className="commitments__when bdc-num">
                  From {formatDate(row.effective_date)}
                  {isCurrent ? <span className="me-pill me-pill--ok">Current</span> : null}
                  {isUpcoming ? <span className="me-pill me-pill--warn">Upcoming</span> : null}
                </div>
                <div className="commitments__what">{describeCommitment(row)}</div>
                {row.notes ? <div className="commitments__notes">{row.notes}</div> : null}
                {editable ? (
                  <button
                    type="button"
                    className="commitments__edit"
                    onClick={() => openEdit(row)}
                    aria-label={`Edit the commitment from ${formatDate(row.effective_date)}`}
                    title="Edit"
                  >
                    <i className="bi bi-pencil" aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        onExited={() => setEditing(null)}
        size="sm"
        backdrop="static"
        keyboard={!confirmDeleteOpen}
      >
        <Modal.Header>
          <Modal.Title>{editing ? 'Edit commitment' : 'Record a commitment change'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError ? (
            <Message type="error" showIcon style={{ marginBottom: 12 }}>{formError}</Message>
          ) : null}

          <div className="commitments__form">
            <label className="commitments__field">
              <span className="me-field__label">Starts with the pay period beginning</span>
              <DatePicker
                value={form.effectiveDate}
                onChange={(value) => setField('effectiveDate', value)}
                oneTap
                cleanable={false}
                isoWeek={false}
                weekStart={0}
                format="MM/dd/yyyy"
                shouldDisableDate={(date) => !isPayPeriodStart(date)}
                block
              />
            </label>

            <div className="commitments__field">
              <span className="me-field__label">Classification</span>
              <div className="bdc-segment" role="group" aria-label="Classification">
                {[
                  { value: false, label: 'Exempt' },
                  { value: true, label: 'Non-exempt' },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={`bdc-btn bdc-btn--sm${form.isPartTime === option.value ? ' bdc-btn--primary' : ''}`}
                    aria-pressed={form.isPartTime === option.value}
                    onClick={() => setField('isPartTime', option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {form.isPartTime ? (
              <label className="commitments__field">
                <span className="me-field__label">Target hours per pay period</span>
                <input
                  type="number"
                  className="form-control"
                  value={form.targetHours}
                  onChange={(e) => setField('targetHours', e.target.value)}
                  min="0"
                  max="200"
                  step="0.25"
                  placeholder="Leave empty for no target"
                />
              </label>
            ) : (
              <label className="commitments__field">
                <span className="me-field__label">% of total available working hours</span>
                <input
                  type="number"
                  className="form-control"
                  list="commitment-percent-options"
                  value={form.tawhPercent}
                  onChange={(e) => setField('tawhPercent', e.target.value)}
                  min="0"
                  max="100"
                  step="0.5"
                  placeholder="100"
                />
                <datalist id="commitment-percent-options">
                  {tawhPercents.map((pct) => <option key={pct} value={String(pct)} />)}
                </datalist>
              </label>
            )}

            <label className="commitments__field">
              <span className="me-field__label">Notes</span>
              <input
                className="form-control"
                value={form.notes}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Optional - e.g. moved up after review"
              />
            </label>
          </div>

          <div className="text-muted small commitments__hint">
            Applies to whole pay periods from this date until the next change. Earlier pay periods keep the
            commitment they had.
            {reachesBack
              ? ' This date is in the past, so View Hours and its checks for those pay periods will be re-measured against it. Saved payroll runs keep the figures they were filed with.'
              : ''}
          </div>
        </Modal.Body>
        <Modal.Footer
          left={editing?.id ? (
            <Button
              variant="danger"
              icon="trash"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={busy || rows.length <= 1}
              title={rows.length <= 1 ? 'An employee needs at least one commitment' : undefined}
            >
              Delete
            </Button>
          ) : null}
        >
          <Button onClick={closeModal} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={createCommitment.isPending || updateCommitment.isPending}>
            {editing ? 'Save changes' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} size="xs" role="alertdialog" backdrop="static">
        <Modal.Header>
          <Modal.Title>Delete commitment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete the commitment from {formatDate(editing?.effective_date)}? The pay periods it covered will be
          measured against the one before it.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleteCommitment.isPending}>
            Cancel
          </Button>
          <Button variant="danger-solid" onClick={remove} loading={deleteCommitment.isPending}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default forwardRef(CommitmentHistory);
